// src/components/ServicesCategorySection.tsx
import React, { memo, useCallback } from "react";

import { Button, CircularProgress } from "@mui/material";
import { Col } from "react-bootstrap";

type ButtonVariant = "text" | "outlined" | "contained";

export type Service = {
  id_action: number;
  label: string;
  type: "url" | "function";
  value: string;
  variant?: ButtonVariant;
};

export type ExtraPowerButtonConfig = {
  href: string;
  target?: "_blank" | "_self";
};

function defaultIconByService(value: number) {
  switch (value) {
    case 1:
      return "change.svg";
    case 2:
      return "credit-card.svg";
    case 3:
      return "invoice.svg";
    case 4:
      return "email.svg";
    case 5:
      return "electricity.svg";
    case 6:
      return "helmet.svg";
    case 7:
      return "gas-tank2.svg";
    case 8:
      return "Wallet.svg";
    case 9:
      return "arrange-square.svg";
    case 10:
      return "invoice.svg";
    case 11:
      return "update.svg";
    case 12:
      return "green-invoice.svg";
    case 13:
      return "danger.svg";
    case 14:
      return "water-heat.svg";
    case 15:
      return "gas-tank.svg";
    case 16:
      return "mobility.svg";
    default:
      return "";
  }
}

const serviceButtonSx = {
  mb: 2,
  borderColor: "primary.main",
  borderRadius: 3,
  color: "primary.main",
  fontWeight: 800,
  fontSize: "1.3rem",
  textTransform: "none",
  pl: 0,
  minHeight: 56,
  position: "relative",
  overflow: "hidden",
  justifyContent: "center",
} as const;

export default memo(function ServicesCategorySection({
  isActive,
  onAvatarClick, // kept for compatibility
  services,
  loading,
  onServiceSelect,
  extraPowerButton,
  friendReferralButton,
  iconBasePath = "svg",
  iconByService = defaultIconByService,
}: {
  isActive: boolean;
  onAvatarClick: () => void;
  services: Service[];
  loading: boolean;
  onServiceSelect?: (srv: Service) => void;
  /** Adds the hardcoded EXTRA POWER link inside the same service grid. */
  extraPowerButton?: ExtraPowerButtonConfig;
  /** Adds the friend referral link inside the service grid. */
  friendReferralButton?: ExtraPowerButtonConfig;
  /** Optional: override icon folder path (defaults to "svg") */
  iconBasePath?: string;
  /** Optional: provide custom icon resolver */
  iconByService?: (id_action: number) => string;
}) {
  const handleClick = useCallback(
    (srv: Service) => {
      // Delegate all service behavior to the parent.
      onServiceSelect?.(srv);
    },
    [onServiceSelect],
  );

  if (!isActive) return null;

  if (loading) {
    return <CircularProgress sx={{ mt: 2 }} />;
  }

  return (
    <div className="row">
      {services.map((srv) => {
        const { id_action, label, value, variant } = srv;
        const iconFile = iconByService(id_action);
        const showIcon = !!iconFile;

        return (
          <Col key={`${label}-${value}-${id_action}`} sm={4}>
            <Button
              fullWidth
              variant={variant ?? "outlined"}
              onClick={() => handleClick(srv)}
              data-action-id={id_action}
              aria-label={label}
              sx={serviceButtonSx}
            >
              <span style={{ color: "black" }}>{label}</span>

              {showIcon && (
                <img
                  src={`${iconBasePath}/${iconFile}`}
                  alt=""
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 4,
                    position: "absolute",
                    right: 25,
                    top: "50%",
                    transform: "translateY(-50%)",
                    pointerEvents: "none",
                  }}
                />
              )}
            </Button>
          </Col>
        );
      })}

      {extraPowerButton && (
        <Col key="extra-power-services-button" sm={4}>
          <Button
            component="a"
            href={extraPowerButton.href}
            target={extraPowerButton.target ?? "_blank"}
            rel={
              (extraPowerButton.target ?? "_blank") === "_blank"
                ? "noopener noreferrer"
                : undefined
            }
            fullWidth
            variant="outlined"
            aria-label="מעבר לארנק ההטבות EXTRA POWER"
            sx={serviceButtonSx}
          >
            <span
              style={{
                direction: "ltr",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                minWidth: 0,
                paddingLeft: 17,
                paddingRight: 17,
                gap: 8,
              }}
            >
              <img
                src="/logo-extra.png"
                alt="EXTRA POWER"
                style={{
                  display: "block",
                  width: 204,
                  maxWidth: "42%",
                  height: 34,
                  objectFit: "contain",
                  flexShrink: 1,
                }}
              />

              <span
                dir="rtl"
                style={{
                  color: "black",
                  fontSize: "1.2rem",
                  fontWeight: 800,
                  lineHeight: 1,
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                }}
              >
                ארנק ההטבות
              </span>

              <img
                src="/NEW-icon.png"
                alt="חדש"
                style={{
                  display: "block",
                  width: 41,
                  height: 42,
                  objectFit: "contain",
                  flexShrink: 0,
                }}
              />
            </span>
          </Button>
        </Col>
      )}

      {friendReferralButton && (
        <Col key="friend-referral-services-button" sm={4}>
          <Button
            component="a"
            href={friendReferralButton.href}
            target={friendReferralButton.target ?? "_blank"}
            rel={
              (friendReferralButton.target ?? "_blank") === "_blank"
                ? "noopener noreferrer"
                : undefined
            }
            fullWidth
            variant="outlined"
            aria-label="חבר מביא חבר"
            sx={serviceButtonSx}
          >
            <span style={{ color: "black" }}>חבר מביא חבר</span>
            <img
              src="/coin.png"
              alt=""
              style={{
                width: 28,
                height: 28,
                borderRadius: 4,
                position: "absolute",
                right: 25,
                top: "50%",
                transform: "translateY(-50%)",
                pointerEvents: "none",
              }}
            />
          </Button>
        </Col>
      )}
    </div>
  );
});
